import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, ReactiveFormsModule, ValidatorFn } from '@angular/forms';
import { distinctUntilChanged, tap } from 'rxjs';
import { PrecreditInputComponent } from '../precredit-input/precredit-input.component';
import { PhoneService } from 'src/core/services/phone/phone.service';
import {
  PhoneRule,
  digitsOnly,
  normalizeDialingCode,
  stripNonNumericPreservingPlus,
} from 'src/core/interfaces/phone.interface';

@Component({
  selector: 'phone-input',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, PrecreditInputComponent],
  templateUrl: './phone-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhoneInputComponent implements OnInit, OnChanges {
  @Input({ required: true }) control!: AbstractControl;
  @Input() isoCode: string = '';
  @Input() dialingCode: string = '';
  @Input() fallbackLabel: string = 'Teléfono';
  @Input() isRequired: boolean = false;

  @Input()
  set invalidMessage(value: string) {
    this.invalidMessageOverride.set(value ?? '');
  }

  private readonly service = inject(PhoneService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly invalidMessageOverride = signal<string>('');

  protected rule = signal<PhoneRule | null>(null);
  protected ruleError = signal<boolean>(false);
  protected serverError = signal<string | null>(null);
  protected lengthInvalid = signal<boolean>(false);
  protected charsInvalid = signal<boolean>(false);

  protected label = computed<string>(() => {
    const base = this.fallbackLabel;
    return this.isRequired ? `${base} *` : base;
  });

  protected helperText = computed<string>(() => {
    const r = this.rule();
    if (!r) return '';
    const dc = normalizeDialingCode(r.dialingCode);
    return dc ? `Prefijo ${dc} (opcional)` : '';
  });

  protected hasError = computed<boolean>(() => {
    return this.lengthInvalid() || this.charsInvalid() || !!this.serverError();
  });

  protected errorMessage = computed<string>(() => {
    if (this.serverError()) return this.serverError() as string;
    const override = this.invalidMessageOverride();
    if (override) return override;
    const r = this.rule();
    if (!r) return 'Número inválido.';
    const x = r.minLength === r.maxLength ? `${r.minLength}` : `${r.minLength}-${r.maxLength}`;
    return `Número inválido. Debe contener ${x} dígitos (0–9) sin espacios ni caracteres especiales.`;
  });

  private readonly phoneValidator: ValidatorFn = (control) => {
    const r = this.rule();
    if (!r) return null;
    const raw = String(control.value ?? '').trim();
    if (!raw) return null;
    const stripped = raw.replace(/^\+/, '');
    if (!/^[0-9]+$/.test(stripped)) return { phoneInvalid: true };
    const ruleDialing = normalizeDialingCode(r.dialingCode).replace(/^\+/, '');
    let national = raw.startsWith('+') ? raw.slice(1) : raw;
    if (ruleDialing && national.startsWith(ruleDialing)) {
      national = national.slice(ruleDialing.length);
    }
    national = digitsOnly(national);
    return national.length >= r.minLength && national.length <= r.maxLength
      ? null
      : { phoneInvalid: true };
  };

  ngOnInit(): void {
    const ctrl = this.control as FormControl | null;
    ctrl?.addValidators(this.phoneValidator);
    this.loadRule();
    this.attachLiveValidation();
    this.attachBlockingRuleWatcher();
    ctrl?.updateValueAndValidity({ emitEvent: false });
  }

  private attachBlockingRuleWatcher(): void {
    const ctrl = this.control as FormControl | null;
    if (!ctrl) return;
    ctrl.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const blockingMessage = ctrl.errors?.['blockingRule'];
        if (blockingMessage) {
          this.serverError.set(blockingMessage);
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['isoCode'] && !changes['isoCode'].firstChange) ||
      (changes['dialingCode'] && !changes['dialingCode'].firstChange)
    ) {
      this.rule.set(null);
      this.ruleError.set(false);
      this.serverError.set(null);
      this.loadRule();
    }
  }

  private loadRule(): void {
    const r = this.dialingCode
      ? this.service.findRuleByDialingCode(this.dialingCode)
      : this.isoCode
        ? this.service.findRuleByIsoCode(this.isoCode)
        : null;

    if (r && r.minLength != null) {
      this.rule.set(r);
      this.ruleError.set(false);
      this.revalidate();
      this.evaluateLive(String((this.control as FormControl)?.value ?? '').trim());
      return;
    }

    // Fallback: reglas todavía no cargadas (edge case). Pedir al backend.
    if (this.dialingCode) {
      this.service
        .getRuleByDialingCode(this.dialingCode)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (rule) => {
            if (rule && (rule as PhoneRule).minLength != null) this.rule.set(rule as PhoneRule);
            else this.ruleError.set(true);
            this.revalidate();
            this.evaluateLive(String((this.control as FormControl)?.value ?? '').trim());
          },
          error: () => this.ruleError.set(true),
        });
      return;
    }
    if (this.isoCode) {
      this.service
        .getRuleByIsoCode(this.isoCode)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (rule) => {
            if (rule) this.rule.set(rule);
            else this.ruleError.set(true);
            this.revalidate();
            this.evaluateLive(String((this.control as FormControl)?.value ?? '').trim());
          },
          error: () => this.ruleError.set(true),
        });
    }
  }

  private revalidate(): void {
    const ctrl = this.control as FormControl | null;
    ctrl?.updateValueAndValidity({ emitEvent: false });
  }

  private attachLiveValidation(): void {
    const ctrl = this.control as FormControl | null;
    if (!ctrl) return;
    ctrl.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged(),
        tap((value: string) => this.sanitizeAndEvaluate(value ?? ''))
      )
      .subscribe();
  }

  private sanitizeAndEvaluate(value: string): void {
    const ctrl = this.control as FormControl | null;
    if (!ctrl) return;
    const sanitized = stripNonNumericPreservingPlus(value);
    if (sanitized !== value) {
      ctrl.setValue(sanitized, { emitEvent: false });
    }
    this.serverError.set(null);
    this.evaluateLive(sanitized);
    this.revalidate();
  }

  private evaluateLive(value: string): void {
    const r = this.rule();
    if (!r) {
      this.lengthInvalid.set(false);
      this.charsInvalid.set(false);
      return;
    }
    const trimmed = String(value).trim();
    if (trimmed.length === 0) {
      this.lengthInvalid.set(false);
      this.charsInvalid.set(false);
      return;
    }
    const stripped = trimmed.replace(/^\+/, '');
    const onlyDigits = /^[0-9]+$/.test(stripped);
    this.charsInvalid.set(!onlyDigits);
    const ruleDialing = normalizeDialingCode(r.dialingCode).replace(/^\+/, '');
    let national = trimmed.startsWith('+') ? trimmed.slice(1) : trimmed;
    if (ruleDialing && national.startsWith(ruleDialing)) {
      national = national.slice(ruleDialing.length);
    }
    national = digitsOnly(national);
    const lengthOk = national.length >= r.minLength && national.length <= r.maxLength;
    this.lengthInvalid.set(!lengthOk);
  }

  /**
   * Validación al submit. Ahora 100% client-side usando reglas en memoria —
   * mantiene la firma Promise para preservar la API del componente.
   */
  validateRemote(): Promise<{ valid: boolean; e164?: string }> {
    const ctrl = this.control as FormControl | null;
    if (!ctrl) return Promise.resolve({ valid: false });

    const raw = String(ctrl.value ?? '').trim();
    if (!raw) {
      if (this.isRequired) {
        this.serverError.set(this.errorMessage());
        return Promise.resolve({ valid: false });
      }
      this.serverError.set(null);
      return Promise.resolve({ valid: true });
    }

    const ruleDialing = normalizeDialingCode(this.rule()?.dialingCode);
    const countryCode = normalizeDialingCode(this.dialingCode) || ruleDialing || this.isoCode;
    if (!countryCode) {
      this.serverError.set('No se pudo determinar el país para validar el teléfono.');
      return Promise.resolve({ valid: false });
    }

    const res = this.service.validateSync({ countryCode, phone: raw });
    if (!res.valid) {
      this.serverError.set(this.errorMessage());
      return Promise.resolve({ valid: false });
    }
    this.serverError.set(null);
    if (res.e164) {
      ctrl.setValue(res.e164, { emitEvent: false });
    }
    return Promise.resolve({ valid: true, e164: res.e164 });
  }
}
